import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, ArrowLeft, Filter, Layers, Vote, Trophy, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_COLORS = { cribado: "#0ea5e9", diferenciacion: "#f59e0b", comite: "#10b981" };
const PHASE_LABELS = { cribado: "Cribado", diferenciacion: "Diferenciación", comite: "Comité" };

export default function NovaConsolidatedPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areaFilter, setAreaFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [onlyCommittee, setOnlyCommittee] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (areaFilter) params.set("area", areaFilter);
      if (phaseFilter) params.set("phase", phaseFilter);
      if (onlyCommittee) params.set("only_committee", "true");
      const r = await fetch(`${BACKEND_URL}/api/nova/consolidated?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Error cargando vista consolidada");
      setData(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); }, [token, areaFilter, phaseFilter, onlyCommittee]); // eslint-disable-line

  const items = data?.items || [];
  const areasByPhase = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      const a = it.area || "Sin área";
      const p = it.phase || "cribado";
      m[a] = m[a] || {};
      m[a][p] = (m[a][p] || 0) + 1;
    });
    return m;
  }, [items]);

  return (
    <div className="space-y-4 p-6" data-testid="nova-consolidated-page">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/nova")} className="gap-1"><ArrowLeft className="w-4 h-4"/> Volver a NOVA</Button>
      </div>

      {/* Hero */}
      <div className="rounded-3xl p-6 md:p-8 text-white" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 40%, #059669 100%)" }}>
        <div className="flex items-center gap-2 text-emerald-200 text-xs uppercase tracking-widest font-semibold mb-1">
          <Layers className="w-4 h-4"/> Vista consolidada · Comité unificado
        </div>
        <h1 className="text-3xl md:text-4xl font-black">Retos cross-área</h1>
        <p className="text-emerald-100/85 mt-2 max-w-3xl text-sm">Todos los retos de las 3 áreas de negocio en una única vista. Filtra por área y fase para preparar un comité consolidado sin cambiar de campaña.</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap p-3 rounded-2xl border border-border bg-muted/20" data-testid="consolidated-filters">
        <Filter className="w-4 h-4 text-muted-foreground"/>
        <label className="text-xs font-semibold">Área:</label>
        <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="text-sm px-2 py-1 rounded border border-border bg-background" data-testid="consolidated-area-filter">
          <option value="">Todas las áreas</option>
          {(data?.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <label className="text-xs font-semibold ml-3">Fase:</label>
        <div className="flex gap-1">
          <button onClick={() => setPhaseFilter("")} className={`text-xs px-2 py-1 rounded ${!phaseFilter ? "bg-emerald-500 text-white" : "bg-muted"}`} data-testid="consolidated-phase-all">Todas</button>
          {(data?.phases || ["cribado", "diferenciacion", "comite"]).map((p) => (
            <button key={p} onClick={() => setPhaseFilter(phaseFilter === p ? "" : p)} className={`text-xs px-2 py-1 rounded ${phaseFilter === p ? "text-white" : "bg-muted"}`} style={{ background: phaseFilter === p ? PHASE_COLORS[p] : undefined }} data-testid={`consolidated-phase-${p}`}>
              {PHASE_LABELS[p]}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-xs ml-3 cursor-pointer">
          <input type="checkbox" checked={onlyCommittee} onChange={(e) => setOnlyCommittee(e.target.checked)} data-testid="consolidated-only-committee" className="accent-emerald-500"/>
          Solo Comité (con votos)
        </label>
        <span className="ml-auto text-xs text-muted-foreground">{items.length} retos</span>
      </div>

      {/* Area x Phase breakdown */}
      {Object.keys(areasByPhase).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="consolidated-area-breakdown">
          {Object.entries(areasByPhase).map(([area, phases]) => (
            <div key={area} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200 mb-1"><Building2 className="w-3.5 h-3.5"/>{area}</div>
              <div className="text-[10px] flex gap-2">
                {["cribado", "diferenciacion", "comite"].map((p) => (
                  <span key={p} className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{background: PHASE_COLORS[p]}}/>
                    {PHASE_LABELS[p][0]} <strong>{phases[p] || 0}</strong>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600"/></div>}

      {!loading && !items.length && (
        <div className="text-center text-muted-foreground text-sm py-10 border border-dashed border-border rounded-2xl">
          Sin retos con estos filtros. Prueba a ampliar la búsqueda.
        </div>
      )}

      {items.length > 0 && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-bold">#</th>
                <th className="text-left px-3 py-2 font-bold">Reto</th>
                <th className="text-left px-3 py-2 font-bold">Área</th>
                <th className="text-left px-3 py-2 font-bold">Fase</th>
                <th className="text-left px-3 py-2 font-bold">Campaña</th>
                <th className="text-right px-3 py-2 font-bold"><Vote className="w-3.5 h-3.5 inline mr-1"/>Score</th>
                <th className="text-right px-3 py-2 font-bold"><Trophy className="w-3.5 h-3.5 inline mr-1"/>Estado</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id} className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/nova/${it.campaign_id}`)} data-testid={`consolidated-row-${i}`}>
                  <td className="px-3 py-2 text-xs font-mono text-muted-foreground">#{it.n}</td>
                  <td className="px-3 py-2 font-semibold max-w-md">
                    <div className="truncate" title={it.title}>{it.title}</div>
                    {it.champion && <div className="text-[10px] text-muted-foreground">👤 {it.champion}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{it.area || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{background: PHASE_COLORS[it.phase] + "20", color: PHASE_COLORS[it.phase]}}>
                      {PHASE_LABELS[it.phase] || it.phase}
                    </span>
                    {it.go_no_go === "go" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 font-bold">GO</span>}
                    {it.go_no_go === "no_go" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 font-bold">NO GO</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[180px]" title={it.campaign_name}>{it.campaign_name}</td>
                  <td className="px-3 py-2 text-right">
                    {it.committee_score.count > 0 ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 font-bold">{it.committee_score.overall}/5 · {it.committee_score.count}</span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    {it.linked_initiative_id ? <span className="text-emerald-600 font-bold">🔗 vinculado</span> : it.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
